<script context="module" lang="ts">
  import { Toasts } from '$lib/stores';
  import { BASE_APP_URL, BASE_APP_STAGING_AWARE_URL, AVAILABLE_EXTERNALS } from '$lib/constants';
  import { fetch } from '$lib/utils';
  import { dayjs } from '@metafy/dates';
  import { captureException } from '@sentry/sveltekit';

  export async function authenticateWithAPI(page: { url: URL; params: Record<string, string> }) {
    const { url, params } = page;
    const query = url.searchParams;
    let [provider, type] = params.provider.split('/');
    provider = provider.replace('-', '_');

    if (query.has('code') && provider in AVAILABLE_EXTERNALS) {
      try {
        const response = await fetch(`${BASE_APP_URL}/auth/proxy/oauth.json`, {
          method: 'post',
          body: JSON.stringify({
            type,
            provider,
            provider_code: query.get('code'),
            redirect: `${BASE_APP_STAGING_AWARE_URL}/auth/external/${provider}/${type}`,
            timezone: dayjs.tz.guess(),
            locale: window.navigator.language || 'en-US',
            otp: query.get('otp') ?? undefined,
            otpRecoveryCode: query.get('otpRecoveryCode') ?? undefined,
            otpRecordId: query.get('otpRecordId') ?? undefined
          })
        });
        const json = await response.json();

        // Bad request or an issue contacting the API?
        if (response!.status >= 400) {
          return {
            provider,
            status: 'error',
            data: {
              code: json.code,
              content: json.message,
              metadata: json.metadata
            }
          };
        }
        return { type, provider, status: json.status, data: json.data };
      } catch (error) {
        captureException(error);
        return {
          provider,
          status: 'error',
          data: {
            content: `There was an issue communicating with ${AVAILABLE_EXTERNALS[provider]}. Please try again.`
          }
        };
      }
    } else {
      return {
        provider,
        status: 'error',
        data: {
          content: `There was an issue communicating with ${AVAILABLE_EXTERNALS[provider]}. Please try again.`
        }
      };
    }
  }

  export async function doWork(page: { url: URL; params: Record<string, string> }) {
    // Grab params
    const state = page.url.searchParams.get('state');

    // Get path information from when this oauth flow was invoked
    const localPath = window.sessionStorage.getItem('mfy-oauth-path');
    window.sessionStorage.removeItem('mfy-oauth-path');

    // Verify that state is correct
    const localState = window.sessionStorage.getItem('mfy-oauth-state');
    if (localState === null || localState !== state) {
      window.sessionStorage.removeItem('mfy-oauth-state');
      return Toasts.push({
        kind: 'error',
        content: "We couldn't log you in because the information returned was invalid. Please try again."
      });
    }
    window.sessionStorage.removeItem('mfy-oauth-state');

    const toast = Toasts.push({
      kind: 'info',
      content: "We're authenticating... Please wait a moment..."
    });

    // Authenticate with the API
    let response = await authenticateWithAPI(page);
    Toasts.remove(toast);

    if (response.status !== 'success') {
      response.data = {
        kind: 'error',
        content: response.data.content,
        code: response.data.code,
        metadata: response.data.metadata
      };
    }
    if (window.opener) {
      // Post data to the owner window
      window.opener.postMessage(response, location.origin);
    } else {
      // TikTok browser does not close this window, so we try a timeout to redirect
      // to the invoking href instead.
      if (localPath && /ByteDance|ByteLo/.test(navigator.userAgent)) {
        setTimeout(() => {
          window.location.assign(localPath);
        }, 500);
      }

      // Return response, this is used for Apple login
      return response;
    }
  }
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import { onMount } from 'svelte';

  import Meta from '$components/Meta.svelte';

  onMount(() => doWork($page));
</script>

<Meta title="Authenticating..." robots="noindex, nofollow" />
